import { Observable, Subject, first, map, switchMap, takeUntil } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ConverterService, LanguageService, OccEndpointsService, PRODUCT_NORMALIZER } from '@spartacus/core';

import { BynderDocument, AutocompleteSuggestion, RepairabilityProduct, OccRepairabilityProduct, DamResponse } from '../models';
import { WebCommonConfig } from '../config/configs/web-common.config';
import { ConfigurationService } from './configuration.service';
import { RepairabilityFlagEnum } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class RepairabilityService implements OnDestroy {
  private documentsSubject = new Subject<BynderDocument[]>();

  private documentsFlag = 1;

  private get config(): WebCommonConfig {
    return this.configService.config;
  }

  protected readonly destroy$: Subject<void> = new Subject();

  constructor(
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converter: ConverterService,
    private readonly configService: ConfigurationService,
    private readonly languageService: LanguageService
  ) {}

  autocompleteSearch(term: string): Observable<AutocompleteSuggestion[]> {
    const url = this.occEndpointsService.buildUrl('repairabilityAutocomplete', { queryParams: { term } });
    return this.http.get<{ termList: AutocompleteSuggestion[] }>(url).pipe(
      map(({ termList }) => termList)
    );
  }

  searchForRepairability(productCommercialCode: string): Observable<RepairabilityProduct> {
    const url = this.occEndpointsService.buildUrl('repairabilitySearch', { queryParams: { productCommercialCode } });
    return this.http.get<OccRepairabilityProduct>(url).pipe(
      map((result) => {
        return result.flag === RepairabilityFlagEnum.PRODUCT_FOUND
          ? { ...result, product: this.converter.convert(result.product, PRODUCT_NORMALIZER) }
          : { ...result, product: undefined };
      })
    );
  }

  getDocumentsFromRepairability(refSAV: string): Observable<BynderDocument[]> {
    if (this.documentsFlag) {
      this.documentsFlag = 0;
      this.languageService.getActive()
        .pipe(
          switchMap((activeLang: string) => {
            const languageFromConfig = this.config?.bynder?.lang?.[activeLang];
            const defaultLanguage = activeLang?.split('_')[0];

            const _activeLang = languageFromConfig || defaultLanguage;
            const url = `${this.config.bynder.baseUrl}/media?` +
              `property_RefSAV=${refSAV.replace('/', '').toUpperCase()}&` +
              `property_language=${_activeLang?.toUpperCase()}`;
            const headers = {
              'Ocp-Apim-Subscription-Key': this.config.bynder.key
            };

            return this.http.get<BynderDocument[]>(url, { headers });
          }),
          takeUntil(this.destroy$)
        )
        .subscribe(
          (documents) => {
            this.documentsFlag = 1;
            this.documentsSubject.next(documents);
          },
          (error) => {
            this.documentsFlag = 1;
            this.documentsSubject.next([]);
            throw new Error(error.statusText);
          }
        );
    }
    return this.documentsSubject.asObservable().pipe(first((documents) => !!documents));
  }

  getCmmfListForCode(refSAV: string): Observable<DamResponse[]> {
    const url = `${this.config.refsav.baseUrl}/refsav?refsav=${refSAV}`;
    const headers = {
      'Ocp-Apim-Subscription-Key': this.config.refsav.key
    };

    return this.http.get<DamResponse[]>(url, { headers });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
